import { Box, chakra, Flex, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { LuCompass, LuFlame } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "stores/AuthStoreProvider";
import { progressOf } from "src/models/progress";
import { useChatStore } from "src/stores/chatStoreContext";
import { ACCENT_GRADIENT } from "src/theme";
import { tap } from "src/utils/haptic";

function firstNameFromEmail(email: string): string {
  const raw = email.split("@")[0].split(/[._+-]/)[0];
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "";
}

/** A gold coin, drawn: no icon set has a good one. */
function Coin() {
  return (
    <Box
      boxSize="16px"
      borderRadius="full"
      background="radial-gradient(circle at 35% 30%, #fff3b0 0%, #ffd23f 35%, #e0a106 75%, #b77a00 100%)"
      boxShadow="inset 0 0 0 1.5px rgba(150,95,0,0.6)"
      flexShrink={0}
    />
  );
}

function Stat({
  icon,
  value,
  label,
  tint,
}: {
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  tint: string;
}) {
  return (
    <Flex
      align="center"
      gap={1.5}
      h="32px"
      px={2.5}
      borderRadius="full"
      bg="elevated"
      borderWidth="1px"
      borderColor="line"
      aria-label={label}
      title={label}
    >
      <Flex color={tint} align="center">
        {icon}
      </Flex>
      <Text fontSize="13px" fontWeight="bold" fontVariantNumeric="tabular-nums">
        {value}
      </Text>
    </Flex>
  );
}

/**
 * Fixed top bar of the tab pages: the profile (tap to open it) with level,
 * and the progress counters. The numbers are display-only for now; see
 * `models/progress.ts`.
 */
export const TopBar = observer(() => {
  const authStore = useAuthStore();
  const chatStore = useChatStore();
  const navigate = useNavigate();

  const email = authStore.user?.data.email ?? "";
  const name = firstNameFromEmail(email) || "You";
  const streak = chatStore.streakDays;
  const met = chatStore.companionsMet;
  const progress = progressOf({
    messagesSent: chatStore.messagesSent,
    companionsMet: met,
    streakDays: streak,
  });

  return (
    <Flex align="center" justify="space-between" gap={3} px={4} w="100%">
      <chakra.button
        type="button"
        onClick={() => {
          tap();
          navigate("/settings");
        }}
        aria-label="Your profile"
        display="flex"
        alignItems="center"
        gap={2.5}
        minW={0}
        textAlign="left"
        _active={{ transform: "scale(0.97)" }}
        transition="transform 0.15s"
      >
        <Box position="relative" flexShrink={0}>
          <Flex
            boxSize="40px"
            borderRadius="full"
            background={ACCENT_GRADIENT}
            align="center"
            justify="center"
            fontWeight="bold"
            fontSize="17px"
            color="white"
            boxShadow="0 4px 14px rgba(255,98,87,0.35)"
          >
            {name.charAt(0).toUpperCase()}
          </Flex>
          {/* Level badge on the avatar. */}
          <Flex
            position="absolute"
            bottom="-3px"
            right="-5px"
            minW="18px"
            h="18px"
            px="4px"
            borderRadius="full"
            bg="white"
            color="black"
            fontSize="10px"
            fontWeight="black"
            align="center"
            justify="center"
            borderWidth="2px"
            borderColor="app"
          >
            {progress.level}
          </Flex>
        </Box>
        <Box minW={0}>
          <Text fontSize="14px" fontWeight="semibold" lineHeight="1.2" truncate>
            {name}
          </Text>
          <Text fontSize="11px" color="muted" lineHeight="1.3" truncate>
            {progress.title}
          </Text>
          <Box
            mt="3px"
            h="3px"
            w="64px"
            borderRadius="full"
            bg="rgba(255,255,255,0.12)"
            overflow="hidden"
          >
            <Box
              h="100%"
              borderRadius="full"
              background={ACCENT_GRADIENT}
              style={{ width: `${Math.max(6, progress.levelProgress * 100)}%` }}
              transition="width 0.6s ease"
            />
          </Box>
        </Box>
      </chakra.button>

      <Flex gap={1.5} flexShrink={0}>
        <Stat
          icon={<LuFlame size={15} />}
          value={streak}
          label={`${streak}-day streak`}
          tint="#ff9f43"
        />
        <Stat
          icon={<LuCompass size={15} />}
          value={`${met}/${chatStore.companions.length}`}
          label={`${met} of ${chatStore.companions.length} companions met`}
          tint="#8ecae6"
        />
        <Stat
          icon={<Coin />}
          value={progress.coins}
          label={`${progress.coins} coins`}
          tint="#ffd23f"
        />
      </Flex>
    </Flex>
  );
});
